import { AddCircleOutlineRounded } from "@mui/icons-material";
import React, { FC, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  useDeleteActivityFromTemplateMutation,
  useDeleteDeliverableFromTemplateMutation,
} from "../../../../api/services/programTemplate/programTemplateApis";
import {
  MuiBox,
  MuiButtonTertiary,
  MuiGrid,
  WarningConfirmationModal,
} from "../../../../components";
import { MuiTabPanel } from "../../../../components/tabs/MuiTabPanel";
import { ITabs, MuiTabs } from "../../../../components/tabs/MuiTabs";
import {
  commonSiteCoreKeys,
  UnitTypesKey,
} from "../../../../helpers/constants/siteCoreKeys";
import {
  getParsedBEErrors,
  hasKey,
  isNotEmpty,
} from "../../../../helpers/utils";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { siteCoreContentLoadingSelector } from "../../../../redux/slices/appState/appStateSelector";
import {
  toggleLoader,
  toggleToast,
} from "../../../../redux/slices/appState/appStateSlice";
import { programTemplateSiteCoreKeys } from "../../../programTemplateSearch/siteCore/SiteCoreKeys";
import {
  ActivityType,
  DeliverableUniqueFiltersTypes,
  ProgramTemplateActivitiesAndDeliverablesProps,
  ProgramTemplateActivitiesFormKey,
  ProgramTemplateActivitiesRowValue,
  ProgramTemplateDeliverablesFormKey,
  ProgramTemplateDeliverablesRowValue,
  UniqueFiltersTypes,
} from "../../modelsAndSchemas/Models";
import { createProgramSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { EditActivity } from "../editActivity/EditActivity";
import { EditDeliverable } from "../editDeliverable/EditDeliverable";
import { ViewActivity } from "../viewActivity/ViewActivity";
import { ViewDeliverables } from "../viewDeliverables/ViewDeliverables";
import { CreateProgramTemplateActivitiesAndDeliverablesWrapper } from "./CreateProgramTemplateActivitiesAndDeliverables.style";
import { CreateProgramTemplateActivitiesTab } from "./tabs/CreateProgramTemplateActivitiesTab";
import { CreateProgramTemplateDeliverablesTab } from "./tabs/CreateProgramTemplateDeliverablesTab";
import { LabelValue } from "../../../../helpers/types/common";
import { ErrorModel } from "../../../../api/services/client/models";
import {
  BE_ERROR_CODES,
  MODULE_PERMISSIONS,
} from "../../../../helpers/constants";
import PermissionWrapper from "../../../../components/permissionWrapper/PermissionWrapper";

export const pageId = "Create-Program-Template-Activities-And-deliverables";

export const CreateProgramTemplateActivitiesAndDeliverables: FC<
  ProgramTemplateActivitiesAndDeliverablesProps
> = ({
  rows,
  deliverableRows,
  onCreateActivityClick,
  successHandler,
  successHandlerDeliverables,
  programTemplateId,
  isViewTemplate = false,
  onCreateDeliverableClick,
  frameWorkId = "",
}) => {
  const tableRef = useRef<{ resetPagination: () => void }>(null);
  const deliverableTableRef = useRef<{ resetPagination: () => void }>(null);
  const [showViewActivity, setShowViewActivity] = useState(false);
  const [showEditActivity, setShowEditActivity] = useState(false);
  const [showEditDeliverables, setShowEditDeliverables] = useState(false);
  const [showViewDeliverables, setShowViewDeliverables] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(
    {} as ProgramTemplateActivitiesRowValue,
  );
  const [selectedDeliverables, setSelectedDeliverables] = useState(
    {} as ProgramTemplateDeliverablesRowValue,
  );

  const { fetchLabel, commonLabels, fetchValueByKeyOfDropdown } = useSiteCore();
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);

  // updated siteCore data
  const [allUpdatedRows, setAllUpdatedRows] = useState<
    ProgramTemplateActivitiesRowValue[]
  >([]);

  const [allUpdatedDeliverablesRows, setAllUpdatedDeliverablesRows] = useState<
    ProgramTemplateDeliverablesRowValue[]
  >([]);
  const [filteredResults, setFilteredResults] = useState([...rows]);
  const [filteredDeliverableResults, setFilteredDeliverableResults] = useState([
    ...deliverableRows,
  ]);
  const [showModal, setShowModal] = useState(false);
  const [showModalFor, setShowModalFor] = useState("");
  const [
    showWarningPopupAlreadyInUseActivity,
    setShowWarningPopupAlreadyInUseActivity,
  ] = useState(false);

  const [uniqueFilters, setUniqueFilters] = useState<UniqueFiltersTypes>({
    [ProgramTemplateActivitiesFormKey.modality]: [],
    [ProgramTemplateActivitiesFormKey.programFramework]: [],
    [ProgramTemplateActivitiesFormKey.units]: [],
  });

  const [uniqueDeliverablesFilters, setUniqueDeliverablesFilters] =
    useState<DeliverableUniqueFiltersTypes>({
      [ProgramTemplateDeliverablesFormKey.activityTypes]: [],
      [ProgramTemplateDeliverablesFormKey.deliverableType]: [],
      [ProgramTemplateDeliverablesFormKey.units]: [],
    });

  const dispatch = useDispatch();

  const {
    mutateAsync: deleteActivityFromProgramTemplate,
    isSuccess,
    isPending: isDeletePending,
    isError: isDeleteActivityError,
    error: deleteActivityError,
  } = useDeleteActivityFromTemplateMutation();

  const {
    mutateAsync: deleteDeliverableFromProgramTemplate,
    isSuccess: isSuccessDeleteDeliverable,
    isPending: isDeleteDeliverablePending,
    isError: isDeleteDeliverableError,
  } = useDeleteDeliverableFromTemplateMutation();

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isDeletePending,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isDeletePending]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isDeleteDeliverablePending,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isDeleteDeliverablePending]);

  useEffect(() => {
    if (isSuccess) {
      dispatch(
        toggleToast({
          open: true,
          message: fetchLabel(
            createProgramSiteCoreKeys.activitiesList,
            createProgramSiteCoreKeys.activityDeletedSuccessfully,
          ),
        }),
      );
      successHandler();
    }
    if (isDeleteActivityError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isSuccess, isDeleteActivityError]);

  const showErrorsFromBE = (error: ErrorModel) => {
    if (
      error.errorCode &&
      error.errorCode ===
        BE_ERROR_CODES.ACTIVITY_ALREADY_IN_USE_PROGRAM_TEMPLATE_ACTIVITY
    ) {
      setShowWarningPopupAlreadyInUseActivity(true);
    }
  };
  useEffect(() => {
    dispatch(toggleLoader({ isVisible: false }));
    if (isDeleteActivityError && deleteActivityError) {
      const error = getParsedBEErrors(deleteActivityError)[0];
      if (isNotEmpty(error) && hasKey(error, "errorCode")) {
        showErrorsFromBE(error);
      } else {
        dispatch(
          toggleToast({
            open: true,
            type: "error",
            message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          }),
        );
      }
    }
  }, [isDeleteActivityError]);

  useEffect(() => {
    if (isSuccessDeleteDeliverable) {
      dispatch(
        toggleToast({
          open: true,
          message: fetchLabel(
            createProgramSiteCoreKeys.deliverablesListKey,
            createProgramSiteCoreKeys.deleteDeliverableSuccess,
          ),
        }),
      );
      successHandlerDeliverables();
    }
    if (isDeleteDeliverableError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isSuccessDeleteDeliverable, isDeleteDeliverableError]);

  const handleWarningPopupAlreadyInUseActivity = () => {
    if (showWarningPopupAlreadyInUseActivity) {
      setShowWarningPopupAlreadyInUseActivity(false);
    }
  };

  const resetActivitiesPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };
  const resetDeliverablesPagination = () => {
    if (deliverableTableRef.current) {
      deliverableTableRef.current.resetPagination();
    }
  };

  const indexRef = useRef<number>();

  const getValueByKeyOfDropdown = (
    masterKey: string,
    parentKey: string,
    key: string,
  ) => {
    return fetchValueByKeyOfDropdown(masterKey, parentKey, key);
  };

  const getUniqueFilters = (
    data: ProgramTemplateActivitiesRowValue[],
    key: string,
  ): LabelValue[] => {
    return Array.from(
      new Set(
        data
          .filter((item: ProgramTemplateActivitiesRowValue) => item[key]) // Filter out null or undefined values
          .map((item: ProgramTemplateActivitiesRowValue) => item[key]) // Extract values for the given key
          .reduce((acc, val) => acc.concat(val), []), // Flatten the array if values are arrays
      ),
    ).map((value) => ({ value: value as string, label: value as string })); // Format as value-label pairs
  };

  const getUniqueDeliverablesFilters = (
    deliverablesData: ProgramTemplateDeliverablesRowValue[],
    key: string,
  ): LabelValue[] => {
    return Array.from(
      new Set(
        deliverablesData
          .filter((item: ProgramTemplateDeliverablesRowValue) => item[key]) // Filter out null or undefined values
          .map((item: ProgramTemplateDeliverablesRowValue) => item[key]) // Extract values for the given key
          .reduce((acc, val) => acc.concat(val), []), // Flatten the array if values are arrays
      ),
    ).map((value) => ({ value: value as string, label: value as string })); // Format as value-label pairs
  };

  const { control, watch, reset, getValues } = useForm({
    mode: "all",
    defaultValues: {
      [ProgramTemplateActivitiesFormKey.programFramework]: "",
      [ProgramTemplateActivitiesFormKey.activityType]: "",
      [ProgramTemplateActivitiesFormKey.modality]: "",
      [ProgramTemplateActivitiesFormKey.topic]: "",
      [ProgramTemplateActivitiesFormKey.activityStatus]: "",
      [ProgramTemplateActivitiesFormKey.units]: "",
      [ProgramTemplateActivitiesFormKey.measurement]: "",
      [ProgramTemplateActivitiesFormKey.candidateStatus]: "",
    },
  });

  const {
    control: deliverableControl,
    watch: deliverableWatch,
    reset: deliverableReset,
    getValues: deliverableGetValues,
  } = useForm({
    mode: "all",
    defaultValues: {
      [ProgramTemplateDeliverablesFormKey.activityTypes]: "",
      [ProgramTemplateDeliverablesFormKey.name]: "",
      [ProgramTemplateDeliverablesFormKey.deliverableType]: "",
      [ProgramTemplateDeliverablesFormKey.units]: "",
      [ProgramTemplateDeliverablesFormKey.measurement]: "",
    },
  });

  const dropdownKeyMapping = {
    modality: createProgramSiteCoreKeys.listModalityList,
    candidateStatus: createProgramSiteCoreKeys.listCandidateStatusList,
    activityStatus: createProgramSiteCoreKeys.listStatusList,
    measurement: createProgramSiteCoreKeys.listMinutesList,
    units: createProgramSiteCoreKeys.listUnitList,
  };

  const deliverableDropdownKeyMapping = {
    deliverableType: createProgramSiteCoreKeys.deliverableTypeList,
    measurement: createProgramSiteCoreKeys.listMinutesList,
    units: createProgramSiteCoreKeys.createDeliverableUnitList,
  };

  const getDropdownValueForKey = (
    key: keyof typeof dropdownKeyMapping,
    data: ProgramTemplateActivitiesRowValue,
  ) => {
    const value = data[key];
    if (key === ProgramTemplateActivitiesFormKey.measurement) {
      if (data.units === UnitTypesKey.Minutes) {
        return Number(
          getValueByKeyOfDropdown(
            createProgramSiteCoreKeys.activitiesList,
            dropdownKeyMapping[key],
            data[key],
          ),
        );
      } else {
        return Number(data[key]);
      }
    }

    // Handle array of values
    if (
      key === ProgramTemplateActivitiesFormKey.modality ||
      key === ProgramTemplateActivitiesFormKey.candidateStatus
    ) {
      if (Array.isArray(value)) {
        return value.map((item) =>
          getValueByKeyOfDropdown(
            createProgramSiteCoreKeys.activitiesList,
            dropdownKeyMapping[key],
            item,
          ),
        );
      }
    } else {
      return getValueByKeyOfDropdown(
        createProgramSiteCoreKeys.activitiesList,
        dropdownKeyMapping[key],
        data[key],
      );
    }
  };

  const getDeliverablesDropdownValueForKey = (
    key: keyof typeof deliverableDropdownKeyMapping,
    data: ProgramTemplateDeliverablesRowValue,
  ) => {
    if (key === ProgramTemplateDeliverablesFormKey.measurement) {
      if (data.units === UnitTypesKey.Minutes) {
        return Number(
          getValueByKeyOfDropdown(
            createProgramSiteCoreKeys.activitiesList,
            deliverableDropdownKeyMapping[key],
            data[key],
          ),
        );
      } else {
        return Number(data[key]);
      }
    }

    return getValueByKeyOfDropdown(
      createProgramSiteCoreKeys.createDeliverableKey,
      deliverableDropdownKeyMapping[key],
      data[key],
    );
  };

  useEffect(() => {
    if (!siteCoreLoading) {
      const localData: ProgramTemplateActivitiesRowValue[] = JSON.parse(
        JSON.stringify(rows),
      );

      localData.forEach((data) => {
        data.activityStatusKey = data.activityStatus;

        Object.keys(dropdownKeyMapping).forEach((key) => {
          data[key] = getDropdownValueForKey(
            key as keyof typeof dropdownKeyMapping,
            data,
          );
        });
      });

      setFilteredResults([...localData]);
      setAllUpdatedRows(localData);

      setUniqueFilters((prevFilters) => ({
        ...prevFilters,
        [ProgramTemplateActivitiesFormKey.modality]: getUniqueFilters(
          localData,
          ProgramTemplateActivitiesFormKey.modality,
        ),
        [ProgramTemplateActivitiesFormKey.programFramework]: getUniqueFilters(
          localData,
          ProgramTemplateActivitiesFormKey.programFramework,
        ),
        [ProgramTemplateActivitiesFormKey.units]: getUniqueFilters(
          localData,
          ProgramTemplateActivitiesFormKey.units,
        ),
      }));

      reset();
      resetActivitiesPagination();
    }
  }, [rows, siteCoreLoading]);

  useEffect(() => {
    if (!siteCoreLoading) {
      const localData: ProgramTemplateDeliverablesRowValue[] = JSON.parse(
        JSON.stringify(deliverableRows),
      );

      localData.forEach((data) => {
        Object.keys(deliverableDropdownKeyMapping).forEach((key) => {
          data[key] = getDeliverablesDropdownValueForKey(
            key as keyof typeof deliverableDropdownKeyMapping,
            data,
          );
        });
      });

      setFilteredDeliverableResults([...localData]);
      setAllUpdatedDeliverablesRows(localData);

      const findUniqueValues = (
        data: ProgramTemplateDeliverablesRowValue[],
        column: string,
      ): any => {
        const uniqueRecords = data
          .filter((item) => item[column]?.length > 0)
          .flatMap((item) =>
            item[column].map((activityType: ActivityType) => activityType.name),
          );

        const resultSet = [...new Set(uniqueRecords)].map((value) => ({
          value,
          label: value,
        }));

        return resultSet;
      };

      setUniqueDeliverablesFilters((prevFilters) => ({
        ...prevFilters,
        [ProgramTemplateDeliverablesFormKey.activityTypes]: findUniqueValues(
          localData,
          ProgramTemplateDeliverablesFormKey.activityTypes,
        ),
        [ProgramTemplateDeliverablesFormKey.deliverableType]:
          getUniqueDeliverablesFilters(
            localData,
            ProgramTemplateDeliverablesFormKey.deliverableType,
          ),
        [ProgramTemplateDeliverablesFormKey.name]: getUniqueDeliverablesFilters(
          localData,
          ProgramTemplateDeliverablesFormKey.name,
        ),
        [ProgramTemplateActivitiesFormKey.units]: getUniqueDeliverablesFilters(
          localData,
          ProgramTemplateActivitiesFormKey.units,
        ),
      }));

      deliverableReset();
      resetDeliverablesPagination();
    }
  }, [deliverableRows, siteCoreLoading]);

  // Helper function for candidateStatus filter
  const candidateStatusMatches = (
    statuses: string[],
    filterValueStr: string,
  ) => {
    return statuses?.some(
      (status: string) => status.toLowerCase() === filterValueStr,
    );
  };

  // Helper function for activityStatus filter
  const activityStatusMatches = (
    itemStatus: string,
    filterValueStr: string,
    activityStatusAll: string,
  ) => {
    return (
      filterValueStr === "" ||
      filterValueStr === activityStatusAll.toLowerCase() ||
      itemStatus?.toString().toLowerCase() === filterValueStr
    );
  };

  const applyFilters = (data: ProgramTemplateActivitiesRowValue[]) => {
    const filters = getValues();

    const activityStatusAll = fetchValueByKeyOfDropdown(
      createProgramSiteCoreKeys.activitiesList,
      createProgramSiteCoreKeys.listStatusList,
      commonSiteCoreKeys.statusAll,
    );

    return data.filter((item) => {
      return Object.keys(filters).every((key) => {
        const filterValue = filters[key as keyof typeof filters];
        const filterValueStr = filterValue.toString().toLowerCase();

        if (item[key] === undefined && filterValue !== "") {
          return false;
        }

        // Skip empty filters
        if (filterValue === "") return true;

        // Handle special case for activityStatus
        if (key === ProgramTemplateActivitiesFormKey.activityStatus) {
          return activityStatusMatches(
            item[key],
            filterValueStr,
            activityStatusAll,
          );
        } else if (key === ProgramTemplateActivitiesFormKey.candidateStatus) {
          return candidateStatusMatches(item[key], filterValueStr);
        }

        const itemValue = item[key]?.toString().toLowerCase();
        return itemValue?.includes(filterValueStr);
      });
    });
  };

  const applyDeliverablesFilters = (
    data: ProgramTemplateDeliverablesRowValue[],
  ) => {
    const filters = deliverableGetValues();

    return data.filter((item) => {
      return Object.keys(filters).every((key) => {
        const filterValue = filters[key as keyof typeof filters];

        const filterValueStr = filterValue.toString().toLowerCase();

        if (item[key] === undefined && filterValue !== "") {
          return false;
        }

        // Skip empty filters
        if (filterValue === "") return true;

        if (key === ProgramTemplateDeliverablesFormKey.activityTypes) {
          return item[key]?.some((activityType: ActivityType) =>
            activityType.name?.toLowerCase().includes(filterValueStr),
          );
        } else {
          const itemValue = item[key]?.toString().toLowerCase();
          return itemValue?.includes(filterValueStr);
        }
      });
    });
  };

  useEffect(() => {
    if (isNotEmpty(allUpdatedRows)) {
      const filteredData = applyFilters(allUpdatedRows);
      setFilteredResults(filteredData);
      resetActivitiesPagination();
    }
  }, [
    watch(ProgramTemplateActivitiesFormKey.activityType),
    watch(ProgramTemplateActivitiesFormKey.modality),
    watch(ProgramTemplateActivitiesFormKey.topic),
    watch(ProgramTemplateActivitiesFormKey.activityStatus),
    watch(ProgramTemplateActivitiesFormKey.programFramework),
    watch(ProgramTemplateActivitiesFormKey.units),
    watch(ProgramTemplateActivitiesFormKey.candidateStatus),
  ]);

  useEffect(() => {
    if (isNotEmpty(allUpdatedDeliverablesRows)) {
      const filteredData = applyDeliverablesFilters(allUpdatedDeliverablesRows);
      setFilteredDeliverableResults(filteredData);
      resetDeliverablesPagination();
    }
  }, [
    deliverableWatch(ProgramTemplateDeliverablesFormKey.name),
    deliverableWatch(ProgramTemplateDeliverablesFormKey.deliverableType),
    deliverableWatch(ProgramTemplateDeliverablesFormKey.units),
    deliverableWatch(ProgramTemplateDeliverablesFormKey.activityTypes),
  ]);

  const onActivityViewClick = (
    data: ProgramTemplateActivitiesRowValue,
    isEdit = false,
  ) => {
    setSelectedActivity(data);
    isEdit ? setShowEditActivity(true) : setShowViewActivity(true);
  };

  const onDeliverableViewClick = (
    data: ProgramTemplateDeliverablesRowValue,
    isEdit = false,
  ) => {
    setSelectedDeliverables(data);
    isEdit ? setShowEditDeliverables(true) : setShowViewDeliverables(true);
  };

  const [selectedTab, setSelectedTab] = useState(0);

  const tabIdActivity = `${pageId}-tab1`;
  const tabIdDeliverable = `${pageId}-tab2`;
  const tabs: ITabs[] = [
    {
      id: tabIdActivity,
      label: fetchLabel(
        programTemplateSiteCoreKeys.activitiesList,
        programTemplateSiteCoreKeys.activityTabText,
      ),
    },
    {
      id: tabIdDeliverable,
      label: fetchLabel(
        programTemplateSiteCoreKeys.activitiesList,
        programTemplateSiteCoreKeys.deliverablesTabText,
      ),
    },
  ];

  const onEditDeliverableClick = () => {
    setShowViewDeliverables(false);
    setShowEditDeliverables(true);
  };

  const onTabChange = (event: React.ChangeEvent<{}>, value: number) => {
    setSelectedTab(value);
  };

  const handleModalClose = () => {
    if (showModal) {
      setShowModal(false);
      setShowModalFor("");
    }
  };

  const handleDeleteClick = () => {
    if (isNotEmpty(indexRef.current)) {
      let idToDelete = indexRef.current;

      if (showModalFor === "Activity") {
        deleteActivityFromProgramTemplate({
          programTemplateId,
          activityId: idToDelete,
        });
      } else if (showModalFor === "Deliverable") {
        deleteDeliverableFromProgramTemplate({
          programTemplateId,
          deliverableId: idToDelete,
        });
      }

      setShowModal(false);
      setShowModalFor("");
    }
  };

  const deleteActivity = (row: any) => {
    if (showModal === false) {
      indexRef.current = row?.id;
      setShowModal(true);
      setShowModalFor("Activity");
    }
  };

  const deleteDeliverable = (row: any) => {
    if (showModal === false) {
      indexRef.current = row?.id;
      setShowModal(true);
      setShowModalFor("Deliverable");
    }
  };

  const onEditActivityClick = () => {
    setShowViewActivity(false);
    setShowEditActivity(true);
  };

  const errorWarning = () => {
    if (showModalFor === "Activity") {
      return fetchLabel(
        createProgramSiteCoreKeys.activitiesList,
        createProgramSiteCoreKeys.deleteActivityConfirmation,
      );
    } else if (showModalFor === "Deliverable") {
      return fetchLabel(
        createProgramSiteCoreKeys.deliverablesListKey,
        createProgramSiteCoreKeys.deleteDeliverableConfirmation,
      );
    }
    return "";
  };

  return (
    <CreateProgramTemplateActivitiesAndDeliverablesWrapper>
      <MuiTabs tabs={tabs} value={selectedTab} onChange={onTabChange}>
        <MuiTabPanel
          panelId={`${pageId}-panel1`}
          value={selectedTab}
          index={0}
          ariaLabelledById={tabIdActivity}
        >
          {!isViewTemplate && (
            <PermissionWrapper
              permission={MODULE_PERMISSIONS.ADD_ACTIVITIES_AND_DELIVERABLES}
            >
              <MuiGrid size={{ xs: 12 }} className="form-header-right">
                <MuiBox>
                  <MuiButtonTertiary
                    data-testid="create-activity"
                    onClick={onCreateActivityClick}
                  >
                    <AddCircleOutlineRounded />
                    {fetchLabel(
                      createProgramSiteCoreKeys.activitiesList,
                      createProgramSiteCoreKeys.createActivity,
                    )}
                  </MuiButtonTertiary>
                </MuiBox>
              </MuiGrid>
            </PermissionWrapper>
          )}

          <CreateProgramTemplateActivitiesTab
            tableRef={tableRef}
            control={control}
            isViewTemplate={isViewTemplate}
            onActivityViewClick={onActivityViewClick}
            filteredResults={filteredResults}
            rows={rows}
            uniqueFilters={uniqueFilters}
            deleteAction={deleteActivity}
          ></CreateProgramTemplateActivitiesTab>
        </MuiTabPanel>

        <MuiTabPanel
          panelId={`${pageId}-panel2`}
          value={selectedTab}
          index={1}
          ariaLabelledById={tabIdDeliverable}
        >
          {!isViewTemplate && (
            <PermissionWrapper
              permission={MODULE_PERMISSIONS.ADD_ACTIVITIES_AND_DELIVERABLES}
            >
              <MuiGrid size={{ xs: 12 }} className="form-header-right">
                <MuiBox>
                  <MuiButtonTertiary
                    data-testid="create-activity"
                    onClick={onCreateDeliverableClick}
                  >
                    <AddCircleOutlineRounded />
                    {fetchLabel(
                      createProgramSiteCoreKeys.deliverablesListKey,
                      createProgramSiteCoreKeys.addDeliverablesLabel,
                    )}
                  </MuiButtonTertiary>
                </MuiBox>
              </MuiGrid>
            </PermissionWrapper>
          )}
          <CreateProgramTemplateDeliverablesTab
            tableRef={deliverableTableRef}
            control={deliverableControl}
            isViewTemplate={isViewTemplate}
            onDeliverableViewClick={onDeliverableViewClick}
            filteredResults={filteredDeliverableResults}
            rows={deliverableRows}
            uniqueFilters={uniqueDeliverablesFilters}
            deleteAction={deleteDeliverable}
          ></CreateProgramTemplateDeliverablesTab>
        </MuiTabPanel>
      </MuiTabs>

      {showViewActivity && (
        <ViewActivity
          show={showViewActivity}
          pageId={pageId}
          selectedActivity={selectedActivity}
          handleCloseModal={() => setShowViewActivity(false)}
          isViewTemplate={isViewTemplate}
          editButtonClick={onEditActivityClick}
        />
      )}
      {showEditActivity && (
        <EditActivity
          show={showEditActivity}
          selectedActivity={selectedActivity}
          handleCloseModal={() => setShowEditActivity(false)}
          pageId={pageId}
          getListOfActivitiesApi={successHandler}
          templateId={programTemplateId ?? ""}
          frameWorkId={frameWorkId}
        />
      )}

      {showViewDeliverables && (
        <ViewDeliverables
          show={showViewDeliverables}
          pageId={pageId}
          handleCloseModal={() => setShowViewDeliverables(false)}
          isViewTemplate={isViewTemplate}
          editButtonClick={() => onEditDeliverableClick()}
          selectedDeliverableId={selectedDeliverables?.id}
          viewTemplateId={programTemplateId ?? ""}
        />
      )}

      {showEditDeliverables && (
        <EditDeliverable
          show={showEditDeliverables}
          pageId={pageId}
          handleCloseModal={() => setShowEditDeliverables(false)}
          selectedDeliverables={selectedDeliverables}
          programTemplateId={programTemplateId ?? ""}
          callApiFunction={successHandlerDeliverables}
        />
      )}

      {showModal && (
        <WarningConfirmationModal
          show={showModal}
          modalTitle={commonLabels.confirmation}
          onClose={handleModalClose}
          onPrimaryButtonClick={handleDeleteClick}
          labelContent={errorWarning()}
          secondaryButtonTitle={commonLabels.no}
        />
      )}
      {showWarningPopupAlreadyInUseActivity && (
        <WarningConfirmationModal
          show={showWarningPopupAlreadyInUseActivity}
          onClose={() => {
            setShowWarningPopupAlreadyInUseActivity(false);
          }}
          onPrimaryButtonClick={handleWarningPopupAlreadyInUseActivity}
          showSecondaryButton={false}
          primaryButtonTitle={commonLabels.ok}
          labelContent={fetchLabel(
            createProgramSiteCoreKeys.activitiesList,
            createProgramSiteCoreKeys.thisActivityCannotBeDeletedBecauseItIsTaggedToAnExistingProgram,
          )}
        />
      )}
    </CreateProgramTemplateActivitiesAndDeliverablesWrapper>
  );
};
